<script>
import store from '@state/store'
import Datepicker from 'vuejs-datepicker'
import VueTagsInput from '@johmun/vue-tags-input'
import recurringForm from '@events/components/recurring-form.vue'
import { Cropper } from 'vue-advanced-cropper'
import {
  OPTIONS as recurringOptions,
  EVENT_MINIMUM_AGES as eventMinimumAges,
  EVENT_DETAILS_VALIDATIONS as validations,
  RECURRING_FREQUENCY_UNIT_VALUES as recurringFrequencyUnitValues,
} from '@events/constant'
import { debounce } from 'debounce'
import {
  getHours,
  getMinutes,
  isAfter,
  parseISO,
  setHours,
  setMinutes,
  startOfTomorrow,
} from 'date-fns'
import isDate from 'lodash/isDate'
import isNull from 'lodash/isNull'
import isArray from 'lodash/isArray'
import { validationMixin } from 'vuelidate'
import { authComputed } from '@state/helpers'
import { maxValue, minValue, required } from 'vuelidate/lib/validators'
import { prepareTagsForVueTagsInput } from '@events/utils'

export default {
  name: 'EventForm',
  components: { Cropper, Datepicker, VueTagsInput, recurringForm },
  mixins: [validationMixin],
  props: {
    event: {
      type: Object,
      default: null,
    },
    isLoading: Boolean,
    isLoadingExit: Boolean,
  },
  data: () => ({
    eventDate: {
      startDate: startOfTomorrow(),
      startTime: setHours(startOfTomorrow(), 9),
      endTime: setHours(startOfTomorrow(), 17),
    },
    validEndTime: true,
    eventForm: {},
    recurringForm: {},
    imageCropperVisible: false,
    toCropImage: false,
    eventCause: '',
    eventTag: '',
    eventDisplayCauses: [],
    eventDisplayTags: [],
    recurringVisible: false,
    eventsFiltersInput: {},
    eventsFilters: {},
    recurringOptions,
    eventMinimumAges,
  }),
  validations() {
    return {
      eventForm: {
        ...validations,
        minPeople: {
          required,
          minValue: minValue(1),
          maxValue: maxValue(this.eventForm.maxPeople - 1),
        },
      },
    }
  },
  computed: {
    ...authComputed,
    groups() {
      return this.$store.state.events.groups || []
    },
    currentCompany() {
      let currentCompany = false
      for (let index = 0; index < this.currentUser.companies.length; index++) {
        if (
          this.currentUser.companies[index].id ===
          parseInt(this.$route.params.companyId)
        ) {
          currentCompany = this.currentUser.companies[index]
        }
      }
      return currentCompany
    },
    cannotEditRecurring() {
      if (!this.event) {
        // new event
        return true
      }
      return (
        this.event.recurringCanEdit === true && !this.event.otherDetails.ongoing
      )
    },
  },
  watch: {
    eventTag: 'getEventTags',
    eventDate: {
      handler() {
        this.validEndTime = isAfter(
          this.eventDate.endTime,
          this.eventDate.startTime
        )
      },
      deep: true,
    },
  },
  created() {
    // Set default values on edit event case
    this.setEventForm()
    this.getEventsFilters()
  },
  methods: {
    setEventForm() {
      const otherDetails = this.event
        ? {
            ...this.event,
            ...this.event.otherDetails,
          }
        : {}

      this.recurringForm = {
        recurringEnabled: otherDetails.recurringEnabled || 0,
        recurringFrequency: otherDetails.recurringFrequency || 1,
        recurringFrequencyUnit:
          otherDetails.recurringFrequencyUnit ||
          recurringFrequencyUnitValues[0].value,
        recurringEndType: otherDetails.recurringEndType || 'never',
        recurringEndValueDate: otherDetails.recurringEndValueDate || new Date(),
      }

      if (otherDetails.recurringEndValueLimit) {
        this.recurringForm = {
          ...this.recurringForm,
          recurringEndValueLimit: otherDetails.recurringEndValueLimit,
        }
      }

      if (otherDetails.recurringOn) {
        this.recurringForm = {
          ...this.recurringForm,
          recurringOn: otherDetails.recurringOn,
        }
      }

      this.eventForm = this.recurringForm

      if (!this.event) {
        this.eventForm.locationName = this.currentCompany.name

        return
      }

      this.eventDate = {
        startDate: parseISO(otherDetails.start),
        startTime: parseISO(otherDetails.start),
        endTime: parseISO(otherDetails.end),
      }

      const causes =
        otherDetails.causes &&
        otherDetails.causes.map(({ id, name }) => ({ id, text: name }))

      const schoolGrade =
        otherDetails.schoolGrade &&
        otherDetails.schoolGrade.map(({ id, name }) => ({ id, text: name }))

      const interactingWith =
        otherDetails.interactingWith &&
        otherDetails.interactingWith.map(({ id, name }) => ({ id, text: name }))

      const tags =
        otherDetails.tags &&
        otherDetails.tags.map(({ id, name }) => ({ id, text: name }))

      this.eventForm = {
        ...otherDetails,
        ...this.recurringForm,
        causes,
        tags,
        schoolGrade,
        interactingWith,
      }
    },
    uploadFiles(event) {
      var input = event.target
      if (input.files && input.files.length) {
        const reader = new FileReader()
        reader.onload = ({ target }) => {
          this.toCropImage = {
            original: target.result,
            cropped: null,
          }
        }
        reader.readAsDataURL(input.files[0])
        this.imageCropperVisible = true
      }
    },
    onImageCropperClose() {
      this.imageCropperVisible = false
    },
    onCropClick() {
      const { canvas } = this.$refs.cropper.getResult()
      this.toCropImage.cropped = canvas.toDataURL()
      this.eventForm = {
        ...this.eventForm,
        imageUrl: this.toCropImage.cropped,
      }
      this.$v.eventForm.imageUrl.$touch()
      this.onImageCropperClose()
    },
    getEventsFiltersItems(filter) {
      return this.eventsFilters[filter] || []
    },
    updateEventsFilters(filter, newTags) {
      this.eventForm = { ...this.eventForm, [filter]: newTags }
    },
    getEventsFilters() {
      return store
        .dispatch('events/fetchEventsFilters')
        .then((data) => (this.eventsFilters = data))
        .catch(() => console.warn('Oh. Something went wrong'))
    },
    updateEventTags(newTags) {
      this.eventDisplayTags = []
      this.eventForm = { ...this.eventForm, tags: newTags }
    },
    getEventTags: debounce(async function getEventTags() {
      if (!this.eventTag) return

      await store
        .dispatch('events/fetchFilteredTags', {
          startingWith: this.eventTag,
        })
        .then(({ data }) => {
          if (!data) return
          this.eventDisplayTags = prepareTagsForVueTagsInput(data)
        })
    }, 500),
    showRecurring(value) {
      if (!value) return
      this.recurringVisible = true
    },
    onRecurringSave(payload) {
      this.recurringForm = payload
      this.eventForm = {
        ...this.eventForm,
        ...this.recurringForm,
      }
    },
    onRecurringClose() {
      this.recurringVisible = false
    },
    getMergedDateWithTime(time, date = this.eventDate.startDate) {
      const parseDate = isDate(date) ? date : parseISO(date)
      return setHours(setMinutes(parseDate, getMinutes(time)), getHours(time))
    },
    onlyDecimalForPeopleCount(e) {
      const keyCode = e.keyCode ? e.keyCode : e.which

      const isDecimal =
        (keyCode >= 48 && keyCode <= 57) ||
        (keyCode >= 96 && keyCode <= 105) ||
        ![46, 110, 190].includes(keyCode)

      if (!isDecimal) e.preventDefault()
    },
    onCancelClick(payload) {
      this.$emit('cancel', payload)
    },
    onCancelEventClick(payload) {
      this.$emit('cancelEvent', payload)
    },
    onDetachEventClick(payload) {
      this.$emit('detachEvent', payload)
    },
    onSaveClick() {
      this.$emit('loading', true)
      const vuelidate = this.$v.eventForm
      vuelidate.$touch()
      // if its still pending or an error is returned do not submit
      if (vuelidate.$pending || vuelidate.$error || !this.validEndTime) {
        if (!this.$v.eventForm.imageUrl.$error) {
          window.scroll({ top: 0, left: 0, behavior: 'smooth' })
        }
        this.$emit('loading', false)
        return
      }
      // to form submit after this

      const event = this.eventForm

      const { startTime, endTime } = this.eventDate
      event.start = this.getMergedDateWithTime(startTime)
      event.end = this.getMergedDateWithTime(endTime)

      // Prepare the payload
      event.tags && event.tags.map(({ id, text }) => ({ id, text }))
      event.userId = this.currentUserId

      const filledProperties = Object.entries(this.eventForm).filter(
        ([key, value]) => !isNull(value)
      )
      const payload = filledProperties.map(([key, value]) => [
        key,
        isArray(value) ? JSON.stringify(value) : value,
      ])

      this.$emit('save', Object.fromEntries(payload))
    },
    onSaveExitClick() {
      this.$emit('loadingExit', true)
      const vuelidate = this.$v.eventForm
      vuelidate.$touch()
      // if its still pending or an error is returned do not submit
      if (vuelidate.$pending || vuelidate.$error || !this.validEndTime) {
        if (!this.$v.eventForm.imageUrl.$error) {
          window.scroll({ top: 0, left: 0, behavior: 'smooth' })
        }
        this.$emit('loadingExit', false)
        return
      }
      // to form submit after this

      const event = this.eventForm

      const { startTime, endTime } = this.eventDate
      event.start = this.getMergedDateWithTime(startTime)
      event.end = this.getMergedDateWithTime(endTime)

      // Prepare the payload
      event.tags && event.tags.map(({ id, text }) => ({ id, text }))
      event.userId = this.currentUserId

      const filledProperties = Object.entries(this.eventForm).filter(
        ([key, value]) => !isNull(value)
      )
      const payload = filledProperties.map(([key, value]) => [
        key,
        isArray(value) ? JSON.stringify(value) : value,
      ])

      this.$emit('saveExit', Object.fromEntries(payload))
      this.$emit('cancel', payload)
    },
  },
}
</script>

<template>
  <div class="grid grid-cols-6 gap-6 event-form">
    <BaseInputText
      id="name"
      v-model="eventForm.name"
      class="col-span-2 col-start-2"
      :vuelidate="$v.eventForm.name"
      label="Title: *"
      required
    />

    <BaseFormGroup id="startDate" label="Start Date:" class="col-span-2">
      <div class="relative">
        <Datepicker
          v-model="eventDate.startDate"
          :disabled-dates="{ to: new Date() }"
          input-class="border cursor-pointer input border-gray-light focus:border-gray"
        />
        <span
          class="absolute inset-y-0 right-0 flex items-center mr-4 pointer-events-none"
        >
          <BaseIcon name="calendar" class="text-blue" :size="22" />
        </span>
      </div>
    </BaseFormGroup>

    <div class="col-span-2 col-start-2">
      <BaseTimePicker v-model="eventDate.startTime" label="Start Time:" />
    </div>

    <div class="col-span-2">
      <BaseTimePicker v-model="eventDate.endTime" label="End Time:" />
      <p v-if="!validEndTime" class="mt-2 text-sm errors text-volunteer_red">
        You entered an invalid end time.
      </p>
    </div>

    <div
      v-if="cannotEditRecurring"
      class="flex items-center col-span-4 col-start-2 space-x-5"
    >
      <span>Is this a recurring event?</span>

      <label
        v-for="{ name, value } in recurringOptions"
        :key="value"
        class="inline-flex items-center cursor-pointer"
      >
        <input
          v-model.number="recurringForm.recurringEnabled"
          class="mr-2 checkbox"
          name="recurring_enabled"
          type="radio"
          :value="value"
          @click="showRecurring(value)"
        />
        <span>{{ name }}</span>
      </label>

      <button
        v-if="recurringForm.recurringEnabled"
        class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3"
        @click="showRecurring(true)"
      >
        Edit
      </button>

      <recurringForm
        v-if="recurringVisible"
        :recurring="recurringForm"
        :start-date="eventDate.startDate"
        @save="onRecurringSave"
        @close="onRecurringClose"
      />
    </div>

    <div
      v-if="!cannotEditRecurring && !event.otherDetails.ongoing"
      class="flex items-center col-span-4 col-start-2 space-x-5 text-gray"
    >
      <p
        >You cannot edit any recurring information for this event because
        previously generated recurring events already have attendees or an event
        has been detached.</p
      >
    </div>

    <div class="col-span-2">
      <BaseTreeSelect
        v-model="eventForm.groupId"
        :vuelidate="$v.eventForm.groupId"
        label="Group: *"
        required
        :options="groups"
      />
    </div>

    <BaseFormGroup
      id="schoolGrade"
      label="School Level: *"
      class="col-span-2"
      :has-errors="$v.eventForm.schoolGrade.$error"
      :vuelidate="$v.eventForm.schoolGrade"
      required
    >
      <VueTagsInput
        v-model="eventsFiltersInput.school_grades"
        :max-tags="1"
        :autocomplete-min-length="0"
        :add-on-blur="false"
        :add-only-from-autocomplete="true"
        :autocomplete-items="getEventsFiltersItems('school_grades')"
        :tags="eventForm.schoolGrade"
        placeholder="Search for School Level"
        @tags-changed="(tags) => updateEventsFilters('schoolGrade', tags)"
      />
    </BaseFormGroup>

    <BaseFormGroup
      id="interactingWith"
      label="Interacting with: *"
      class="col-span-2"
      :has-errors="$v.eventForm.interactingWith.$error"
      :vuelidate="$v.eventForm.interactingWith"
      required
    >
      <VueTagsInput
        v-model="eventsFiltersInput.interacting_with"
        :max-tags="1"
        :autocomplete-min-length="0"
        :add-on-blur="false"
        :add-only-from-autocomplete="true"
        :autocomplete-items="getEventsFiltersItems('interacting_with')"
        :tags="eventForm.interactingWith"
        placeholder="Search for interacting"
        @tags-changed="(tags) => updateEventsFilters('interactingWith', tags)"
      />
    </BaseFormGroup>

    <div class="col-span-2">
      <BaseInputText
        id="maxPeople"
        v-model.number="eventForm.maxPeople"
        :vuelidate="$v.eventForm.maxPeople"
        label="Maximum number of people: *"
        min="0"
        required
        type="number"
        @keypress="onlyDecimalForPeopleCount"
        @paste="(e) => e.preventDefault()"
      />
    </div>

    <div class="col-span-2">
      <BaseInputText
        id="minPeople"
        v-model.number="eventForm.minPeople"
        :vuelidate="$v.eventForm.minPeople"
        label="Minimum Volunteers Needed: *"
        min="0"
        required
        type="number"
        @keypress="onlyDecimalForPeopleCount"
        @paste="(e) => e.preventDefault()"
      >
        <template v-if="$v.eventForm.minPeople.required" v-slot:errors>
          <p v-if="!$v.eventForm.minPeople.minValue">
            This should be at least one person
          </p>
          <p v-if="!$v.eventForm.minPeople.maxValue">
            This should be less than maximum number of people
          </p>
        </template>
      </BaseInputText>
    </div>

    <div class="col-span-2">
      <BaseSelectBox
        id="minAge"
        v-model="eventForm.minAge"
        :options="eventMinimumAges"
        :vuelidate="$v.eventForm.minAge"
        label="Minimum age of volunteer: *"
        required
      />
    </div>

    <div class="col-span-2">
      <BaseInputText
        id="locationName"
        v-model="eventForm.locationName"
        :vuelidate="$v.eventForm.locationName"
        label="Location name: *"
        required
      />
    </div>

    <div class="col-span-4">
      <BaseInputText
        id="address"
        v-model="eventForm.address1"
        :vuelidate="$v.eventForm.address1"
        label="Address: *"
        required
      />
    </div>

    <div class="col-span-2">
      <BaseInputText
        id="city"
        v-model="eventForm.city"
        :vuelidate="$v.eventForm.city"
        label="City: *"
        required
      />
    </div>

    <div class="col-span-2">
      <BaseInputText
        id="state"
        v-model="eventForm.state"
        :vuelidate="$v.eventForm.state"
        label="State: *"
        required
      />
    </div>

    <div class="col-span-2">
      <BaseInputText
        id="zip"
        v-model="eventForm.zip"
        :vuelidate="$v.eventForm.zip"
        label="Zip Code: *"
        required
      />
    </div>

    <div class="col-span-3">
      <BaseInputText
        id="whatToDo"
        v-model="eventForm.whatToDo"
        label="What We Will Do:"
        :rows="5"
      />
    </div>

    <div class="col-span-3">
      <BaseInputText
        id="pointOfContact"
        v-model="eventForm.pointOfContact"
        label="Point of contact: *"
        :rows="5"
        :vuelidate="$v.eventForm.pointOfContact"
        required
      />
    </div>

    <div class="col-span-3">
      <BaseInputText
        id="agenda"
        v-model="eventForm.agenda"
        label="Agenda:"
        :rows="5"
      />
    </div>

    <div class="col-span-3">
      <BaseInputText
        id="whatToBring"
        v-model="eventForm.whatToBring"
        label="What to Wear/Bring:"
        :rows="5"
      />
    </div>

    <div class="col-span-3">
      <BaseInputText
        id="whereToPark"
        v-model="eventForm.whereToPark"
        label="Where To Park:"
        :rows="5"
      />
    </div>

    <div class="col-span-3">
      <BaseInputText
        id="whereToMeet"
        v-model="eventForm.whereToMeet"
        label="Where To Meet:"
        :rows="5"
      />
    </div>

    <div class="col-span-3">
      <BaseInputText
        id="details"
        v-model="eventForm.details"
        label="Other Details:"
        :rows="5"
      />
    </div>

    <BaseFormGroup
      :vuelidate="$v.eventForm.imageUrl"
      :has-errors="!eventForm.imageUrl && $v.eventForm.imageUrl.$error"
      class="col-span-2 col-start-1"
      label="Upload an Event Photo or Video: *"
      required
    >
      <button
        class="text-left input bg-gray-light text-gray"
        :class="
          !eventForm.imageUrl && $v.eventForm.imageUrl.$error
            ? 'border-volunteer_red'
            : 'border-gray-light'
        "
      >
        <label class="relative block" for="imageUrl">
          <span>Choose File</span>
          <span class="absolute inset-y-0 right-0 flex items-center pb-1 mr-4">
            <BaseIcon name="upload" class="text-blue" :size="22" />
          </span>
        </label>
      </button>
      <input
        id="imageUrl"
        accept="image/*,video/*"
        class="hidden"
        multiple
        type="file"
        @change="uploadFiles($event)"
      />

      <label
        for="imageUrl"
        class="block w-1/4 mt-3 overflow-hidden transition-opacity duration-150 shadow-lg cursor-pointer rounded-input hover:opacity-75"
      >
        <img v-if="eventForm.imageUrl" :src="eventForm.imageUrl" alt="" />
        <img
          v-if="!eventForm.imageUrl"
          src="https://uploads-pencilmein.s3-us-west-1.amazonaws.com/images/default/event.jpg"
          alt=""
        />
      </label>

      <span class="text-sm mt-2 leading-3 block"
        >Attach a photo or video pertaining to your school/opportunity to
        increase attention to your posting!
      </span>

      <BaseModalWindow
        v-if="imageCropperVisible"
        modalcls="bg-white rounded-xl mx-auto px-11 shadow-box relative left-0 flex justify-between overflow-hidden"
        :visible="imageCropperVisible"
        @close="onImageCropperClose"
      >
        <template v-slot:body>
          <div class="w-full mx-auto mt-12 mb-5">
            <div class="">
              <Cropper
                v-if="toCropImage"
                ref="cropper"
                :stencil-props="{
                  aspectRatio: 1.81 / 1,
                }"
                classname="w-full"
                :src="toCropImage.original"
              />
            </div>

            <div class="mt-3">
              <button
                class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3"
                @click="onCropClick"
              >
                Done
              </button>
            </div>
          </div>
        </template>
      </BaseModalWindow>
    </BaseFormGroup>

    <BaseFormGroup id="causes" label="Opportunity Type:" class="col-span-2">
      <VueTagsInput
        v-model="eventsFiltersInput.causes"
        :autocomplete-min-length="0"
        :add-on-blur="false"
        :add-only-from-autocomplete="true"
        :autocomplete-items="getEventsFiltersItems('causes')"
        :tags="eventForm.causes"
        placeholder="Search for opportunity types"
        @tags-changed="(tags) => updateEventsFilters('causes', tags)"
      />
    </BaseFormGroup>

    <div class="col-span-2">
      <label class="block mb-3 cursor-pointer">
        Tags:
      </label>

      <VueTagsInput
        v-model="eventTag"
        :tags="eventForm.tags"
        :autocomplete-items="eventDisplayTags"
        :add-only-from-autocomplete="true"
        :add-on-blur="false"
        placeholder="Search for tags"
        @tags-changed="updateEventTags"
      />
    </div>

    <div class="col-span-4">
      <button
        class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3"
        :class="{ 'text-gray bg-gray': isLoading }"
        @click="onSaveClick"
        v-text="isLoading ? 'Saving...' : 'Save & Continue'"
      />

      <button
        v-if="event"
        class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3 ml-3"
        :class="{ 'text-gray bg-gray': isLoadingExit }"
        @click="onSaveExitClick"
        v-text="isLoadingExit ? 'Saving...' : 'Save & Exit'"
      />

      <button
        class="button secondary-button h-7.5 leading-relax text-sml rounded-small-button px-3"
        @click="onCancelClick"
      >
        Cancel
      </button>
    </div>
    <div v-if="event" class="col-span-2 text-right">
      <button
        class="button bg-red-500 h-7.5 leading-relax text-sml rounded-small-button px-3 text-white"
        @click="onCancelEventClick"
      >
        Cancel Volunteer Opportunity
      </button>
      <button
        v-if="event.otherDetails && event.otherDetails.recurringEnabled"
        class="button bg-blue-darker h-7.5 leading-relax text-sml rounded-small-button px-3 text-white ml-3"
        @click="onDetachEventClick"
      >
        Detach Volunteer Opportunity
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable */
.event-form::v-deep .vue-tags-input .ti-autocomplete {
  position: absolute !important;
  border: 1px solid theme('colors.gray.lighter') !important;
  @apply shadow-input py-2 rounded-md;
}
/* stylelint-enable */
</style>
